import { 
  Building, Home, MapPin, Key, TrendingUp, Calculator, FileText, Users, 
  Globe, Shield, Zap, Target, Settings, Database, Search, Wrench, 
  Clock, BarChart3, CheckCircle, Award, Smartphone, Cpu, Network, 
  Lock, Eye, Server, AlertTriangle
} from 'lucide-react';
import { ProductConfigs } from './types';

export const realEstateSolutionsConfigs: ProductConfigs = {
  'property-management-system': {
    keyBenefits: [
      {
        icon: Building,
        title: 'Comprehensive Property Operations',
        description: 'Complete property management with tenant screening, lease management, maintenance coordination, and financial reporting',
        metrics: '45% operational efficiency gain',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: TrendingUp,
        title: 'Automated Rent Collection & Financial Management',
        description: 'Intelligent rent collection with automated billing, late payment tracking, and comprehensive financial analytics',
        metrics: '98% collection efficiency',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Users,
        title: 'Enhanced Tenant Experience Platform',
        description: 'Tenant portal with maintenance requests, communication tools, lease management, and payment processing',
        metrics: '65% tenant satisfaction improvement',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Wrench,
        title: 'Intelligent Maintenance Management',
        description: 'Proactive maintenance scheduling with vendor coordination, work order tracking, and preventive care alerts',
        metrics: '40% maintenance cost reduction',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: BarChart3,
        title: 'Portfolio Performance Analytics',
        description: 'Advanced property analytics with performance benchmarking, vacancy tracking, and investment ROI analysis',
        metrics: '50% better investment decisions',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: CheckCircle,
        title: 'Compliance & Risk Management',
        description: 'Automated compliance tracking, safety inspections, insurance management, and regulatory reporting',
        metrics: '99% compliance accuracy',
        color: 'from-indigo-500 to-purple-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Cpu,
        title: 'AI-Powered Property Intelligence',
        description: 'Machine learning algorithms for predictive maintenance, tenant screening, and market analysis',
        metrics: 'Predictive property management',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Database,
        title: 'Smart Property Data Hub',
        description: 'Centralised property database with IoT integration, real-time monitoring, and automated reporting',
        metrics: 'Complete property visibility',
        color: 'from-emerald-500 to-teal-600'
      },
      {
        icon: Smartphone,
        title: 'Mobile-First Management',
        description: 'Native mobile apps for property managers and tenants with offline capability and instant notifications',
        metrics: 'Mobile property control',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Target,
        title: 'Revenue Optimization Engine',
        description: 'Dynamic pricing algorithms, market analysis tools, and vacancy cost optimization',
        metrics: '35% revenue increase',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Globe,
        title: 'Multi-Property Operations Hub',
        description: 'Centralised management for property portfolios with standardised processes and consolidated reporting',
        metrics: 'Unlimited portfolio scale',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: Search,
        title: 'Advanced Analytics & Forecasting',
        description: 'Predictive analytics for market trends, tenant behavior, and property performance optimization',
        metrics: 'Future-ready insights',
        color: 'from-orange-500 to-amber-600'
      }
    ],
    securityPerformance: [
      {
        icon: Award,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, GDPR, and industry-specific compliance with automated audit trails',
        metrics: 'Enterprise-grade compliance',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time security monitoring with threat detection, vulnerability assessments, and compliance reporting',
        metrics: '99.9% threat prevention',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Server,
        title: 'Scalable Security Infrastructure',
        description: 'Auto-scaling security architecture with load balancing, redundancy, and global content delivery',
        metrics: '99.99% uptime guarantee',
        color: 'from-purple-500 to-violet-600'
      },
      {
        icon: Shield,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Multi-layer security with intelligent threat detection, automated response, and traffic filtering',
        metrics: 'Impenetrable security barrier',
        color: 'from-red-500 to-rose-600'
      },
      {
        icon: Lock,
        title: 'Multi-Factor Authentication Suite',
        description: 'Comprehensive MFA with biometric options, hardware tokens, and adaptive authentication policies',
        metrics: '100% identity verification',
        color: 'from-orange-500 to-amber-600'
      },
      {
        icon: Eye,
        title: 'Security Information & Event Management',
        description: '24/7 SIEM monitoring with AI-powered anomaly detection and automated incident response',
        metrics: 'Proactive threat management',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Database,
        title: 'Data Loss Prevention & Backup',
        description: 'Encrypted data protection with automated backups, point-in-time recovery, and geo-redundancy',
        metrics: '100% data recovery guarantee',
        color: 'from-teal-500 to-emerald-600'
      },
      {
        icon: Network,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmented networks with zero-trust architecture and continuous traffic analysis',
        metrics: 'Zero-trust security model',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: AlertTriangle,
        title: 'Continuous Security Assessment',
        description: 'Ongoing security audits, penetration testing, and vulnerability management with remediation tracking',
        metrics: 'Continuous security validation',
        color: 'from-yellow-500 to-orange-600'
      }
    ]
  },

  'real-estate-crm': {
    keyBenefits: [
      {
        icon: Users,
        title: 'Intelligent Lead Management & Scoring',
        description: 'Advanced CRM with AI-powered lead scoring, automated follow-up, and conversion tracking for maximum sales efficiency',
        metrics: '60% lead conversion improvement',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: TrendingUp,
        title: 'Sales Pipeline Optimization Engine',
        description: 'AI-powered sales pipeline with predictive analytics, opportunity scoring, and automated deal progression',
        metrics: '45% sales cycle acceleration',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Target,
        title: 'Client Relationship Excellence Platform',
        description: 'Comprehensive client management with communication tracking, preference recording, and service automation',
        metrics: '70% client satisfaction increase',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: MapPin,
        title: 'Property Matching Intelligence',
        description: 'Smart property matching with client preferences, automated listings distribution, and learning algorithms',
        metrics: '80% match accuracy rate',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: BarChart3,
        title: 'Market Intelligence & Analytics',
        description: 'Comprehensive market analysis with CMA generation, pricing intelligence, and trend forecasting',
        metrics: '90% market prediction accuracy',
        color: 'from-cyan-500 to-teal-600'
      },
      {
        icon: Globe,
        title: 'Multi-Channel Marketing Automation',
        description: 'Integrated marketing campaigns with social media, email automation, and listing syndication',
        metrics: '200% marketing reach expansion',
        color: 'from-pink-500 to-purple-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Cpu,
        title: 'AI-Powered Client Intelligence',
        description: 'Machine learning algorithms for client behavior analysis, preference prediction, and personalized service delivery',
        metrics: 'Predictive client insights',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Database,
        title: 'Comprehensive CRM Data Hub',
        description: 'Centralised client database with interaction history, document management, and automated data enrichment',
        metrics: 'Complete client profiles',
        color: 'from-emerald-500 to-teal-600'
      },
      {
        icon: Smartphone,
        title: 'Mobile CRM Excellence',
        description: 'Native mobile CRM with offline capability, location services, and instant client communication',
        metrics: 'Mobile sales productivity',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Search,
        title: 'Advanced Property Search & Matching',
        description: 'AI-powered property search with natural language processing and automated client notifications',
        metrics: 'Intelligent property discovery',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Clock,
        title: 'Automated Workflow Engine',
        description: 'Customizable workflows with automated tasks, follow-up sequences, and transaction management',
        metrics: '60% time saving automation',
        color: 'from-orange-500 to-amber-600'
      },
      {
        icon: Globe,
        title: 'Integration & API Platform',
        description: 'Seamless integrations with MLS systems, marketing platforms, and third-party real estate tools',
        metrics: 'Unified real estate ecosystem',
        color: 'from-pink-500 to-rose-600'
      }
    ],
    securityPerformance: [
      {
        icon: Award,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, GDPR, and industry-specific compliance with automated audit trails',
        metrics: 'Enterprise-grade compliance',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time security monitoring with threat detection, vulnerability assessments, and compliance reporting',
        metrics: '99.9% threat prevention',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Server,
        title: 'Scalable Security Infrastructure',
        description: 'Auto-scaling security architecture with load balancing, redundancy, and global content delivery',
        metrics: '99.99% uptime guarantee',
        color: 'from-purple-500 to-violet-600'
      },
      {
        icon: Shield,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Multi-layer security with intelligent threat detection, automated response, and traffic filtering',
        metrics: 'Impenetrable security barrier',
        color: 'from-red-500 to-rose-600'
      },
      {
        icon: Lock,
        title: 'Multi-Factor Authentication Suite',
        description: 'Comprehensive MFA with biometric options, hardware tokens, and adaptive authentication policies',
        metrics: '100% identity verification',
        color: 'from-orange-500 to-amber-600'
      },
      {
        icon: Eye,
        title: 'Security Information & Event Management',
        description: '24/7 SIEM monitoring with AI-powered anomaly detection and automated incident response',
        metrics: 'Proactive threat management',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Database,
        title: 'Data Loss Prevention & Backup',
        description: 'Encrypted data protection with automated backups, point-in-time recovery, and geo-redundancy',
        metrics: '100% data recovery guarantee',
        color: 'from-teal-500 to-emerald-600'
      },
      {
        icon: Network,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmented networks with zero-trust architecture and continuous traffic analysis',
        metrics: 'Zero-trust security model',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: AlertTriangle,
        title: 'Continuous Security Assessment',
        description: 'Ongoing security audits, penetration testing, and vulnerability management with remediation tracking',
        metrics: 'Continuous security validation',
        color: 'from-yellow-500 to-orange-600'
      }
    ]
  },

  'property-valuation-platform': {
    keyBenefits: [
      {
        icon: Calculator,
        title: 'AI-Powered Automated Valuation Models',
        description: 'Advanced machine learning algorithms using comparable sales, market trends, and property characteristics for precise valuations',
        metrics: '95% valuation accuracy rate',
        color: 'from-purple-500 to-indigo-600'
      },
      {
        icon: TrendingUp,
        title: 'Comprehensive Market Intelligence',
        description: 'Real-time market analysis with price forecasting, neighbourhood analytics, and investment performance insights',
        metrics: '50% faster market analysis',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Target,
        title: 'Automated CMA & Report Generation',
        description: 'Instant comparative market analysis with professional reports, charts, and detailed valuation documentation',
        metrics: '90% time saving on reports',
        color: 'from-green-500 to-teal-600'
      },
      {
        icon: BarChart3,
        title: 'Investment Analysis & ROI Calculations',
        description: 'Advanced investment analytics with cash flow projections, ROI calculations, and risk assessments',
        metrics: '40% better investment decisions',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: MapPin,
        title: 'Location Intelligence & Scoring',
        description: 'Advanced location analytics with walkability scores, amenity ratings, and neighbourhood trend analysis',
        metrics: 'Comprehensive location insights',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Globe,
        title: 'Multi-Market Valuation Platform',
        description: 'Global property valuation with local market expertise, currency handling, and regional compliance',
        metrics: 'Worldwide valuation capability',
        color: 'from-pink-500 to-purple-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Cpu,
        title: 'Machine Learning Valuation Engine',
        description: 'Continuously learning AI models that improve accuracy with market feedback and transaction data',
        metrics: 'Self-improving accuracy',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Database,
        title: 'Comprehensive Property Data Hub',
        description: 'Extensive property database with historical sales, market trends, and predictive analytics integration',
        metrics: 'Complete property intelligence',
        color: 'from-emerald-500 to-teal-600'
      },
      {
        icon: Search,
        title: 'Advanced Comparable Search',
        description: 'Intelligent comparable property identification with similarity scoring and market adjustment factors',
        metrics: 'Perfect comparable matching',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: BarChart3,
        title: 'Predictive Market Analytics',
        description: 'Future market value predictions with confidence intervals and scenario analysis capabilities',
        metrics: 'Market forecasting accuracy',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Globe,
        title: 'API Integration Platform',
        description: 'Seamless MLS integrations, third-party data sources, and white-label valuation services',
        metrics: 'Universal data connectivity',
        color: 'from-orange-500 to-amber-600'
      },
      {
        icon: FileText,
        title: 'Professional Report Generation',
        description: 'Customizable valuation reports with branded templates, interactive charts, and client portals',
        metrics: 'Professional presentation quality',
        color: 'from-pink-500 to-rose-600'
      }
    ],
    securityPerformance: [
      {
        icon: Award,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, GDPR, and industry-specific compliance with automated audit trails',
        metrics: 'Enterprise-grade compliance',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time security monitoring with threat detection, vulnerability assessments, and compliance reporting',
        metrics: '99.9% threat prevention',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Server,
        title: 'Scalable Security Infrastructure',
        description: 'Auto-scaling security architecture with load balancing, redundancy, and global content delivery',
        metrics: '99.99% uptime guarantee',
        color: 'from-purple-500 to-violet-600'
      },
      {
        icon: Shield,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Multi-layer security with intelligent threat detection, automated response, and traffic filtering',
        metrics: 'Impenetrable security barrier',
        color: 'from-red-500 to-rose-600'
      },
      {
        icon: Lock,
        title: 'Multi-Factor Authentication Suite',
        description: 'Comprehensive MFA with biometric options, hardware tokens, and adaptive authentication policies',
        metrics: '100% identity verification',
        color: 'from-orange-500 to-amber-600'
      },
      {
        icon: Eye,
        title: 'Security Information & Event Management',
        description: '24/7 SIEM monitoring with AI-powered anomaly detection and automated incident response',
        metrics: 'Proactive threat management',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Database,
        title: 'Data Loss Prevention & Backup',
        description: 'Encrypted data protection with automated backups, point-in-time recovery, and geo-redundancy',
        metrics: '100% data recovery guarantee',
        color: 'from-teal-500 to-emerald-600'
      },
      {
        icon: Network,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmented networks with zero-trust architecture and continuous traffic analysis',
        metrics: 'Zero-trust security model',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: AlertTriangle,
        title: 'Continuous Security Assessment',
        description: 'Ongoing security audits, penetration testing, and vulnerability management with remediation tracking',
        metrics: 'Continuous security validation',
        color: 'from-yellow-500 to-orange-600'
      }
    ]
  },

  'short-term-rental-management': {
    keyBenefits: [
      {
        icon: Key,
        title: 'Multi-Platform Booking Synchronization',
        description: 'Seamless synchronization across Airbnb, Booking.com, VRBO, and other platforms with real-time availability updates',
        metrics: '99% booking accuracy across platforms',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: TrendingUp,
        title: 'Dynamic Pricing & Revenue Optimization',
        description: 'AI-powered pricing algorithms that maximize revenue based on demand, seasonality, and competitor analysis',
        metrics: '35% revenue increase on average',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Users,
        title: 'Guest Communication & Experience Hub',
        description: 'Automated guest messaging, check-in instructions, local recommendations, and 24/7 support system',
        metrics: '95% guest satisfaction rate',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Clock,
        title: 'Automated Operations Management',
        description: 'Smart scheduling for cleaning, maintenance, and turnovers with vendor coordination and quality checks',
        metrics: '50% operational efficiency gain',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: BarChart3,
        title: 'Financial Performance Analytics',
        description: 'Comprehensive revenue tracking, expense management, and profitability analysis with tax documentation',
        metrics: 'Complete financial transparency',
        color: 'from-cyan-500 to-teal-600'
      },
      {
        icon: CheckCircle,
        title: 'Review & Reputation Management',
        description: 'Automated review monitoring, response management, and reputation optimization across all platforms',
        metrics: '4.8+ average rating maintenance',
        color: 'from-indigo-500 to-purple-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Cpu,
        title: 'AI-Powered Revenue Intelligence',
        description: 'Machine learning algorithms for optimal pricing, demand forecasting, and market opportunity identification',
        metrics: 'Predictive revenue optimization',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Smartphone,
        title: 'Smart Property Management',
        description: 'IoT integration with smart locks, thermostats, and sensors for remote property monitoring and control',
        metrics: 'Connected property automation',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Globe,
        title: 'Channel Manager Excellence',
        description: 'Advanced channel management with listing optimization, photo management, and content syndication',
        metrics: 'Maximum market visibility',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Target,
        title: 'Guest Experience Automation',
        description: 'Personalized guest journeys with automated upsells, local experience recommendations, and loyalty programs',
        metrics: '40% increase in guest lifetime value',
        color: 'from-orange-500 to-amber-600'
      },
      {
        icon: Database,
        title: 'Comprehensive Analytics Dashboard',
        description: 'Real-time performance metrics, market analysis, and competitive intelligence for data-driven decisions',
        metrics: 'Complete business intelligence',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: Wrench,
        title: 'Maintenance & Quality Assurance',
        description: 'Predictive maintenance scheduling, quality checklists, and vendor performance tracking',
        metrics: '99% property readiness rate',
        color: 'from-teal-500 to-emerald-600'
      }
    ],
    securityPerformance: [
      {
        icon: Award,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, GDPR, and industry-specific compliance with automated audit trails',
        metrics: 'Enterprise-grade compliance',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time security monitoring with threat detection, vulnerability assessments, and compliance reporting',
        metrics: '99.9% threat prevention',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Server,
        title: 'Scalable Security Infrastructure',
        description: 'Auto-scaling security architecture with load balancing, redundancy, and global content delivery',
        metrics: '99.99% uptime guarantee',
        color: 'from-purple-500 to-violet-600'
      },
      {
        icon: Shield,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Multi-layer security with intelligent threat detection, automated response, and traffic filtering',
        metrics: 'Impenetrable security barrier',
        color: 'from-red-500 to-rose-600'
      },
      {
        icon: Lock,
        title: 'Multi-Factor Authentication Suite',
        description: 'Comprehensive MFA with biometric options, hardware tokens, and adaptive authentication policies',
        metrics: '100% identity verification',
        color: 'from-orange-500 to-amber-600'
      },
      {
        icon: Eye,
        title: 'Security Information & Event Management',
        description: '24/7 SIEM monitoring with AI-powered anomaly detection and automated incident response',
        metrics: 'Proactive threat management',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Database,
        title: 'Data Loss Prevention & Backup',
        description: 'Encrypted data protection with automated backups, point-in-time recovery, and geo-redundancy',
        metrics: '100% data recovery guarantee',
        color: 'from-teal-500 to-emerald-600'
      },
      {
        icon: Network,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmented networks with zero-trust architecture and continuous traffic analysis',
        metrics: 'Zero-trust security model',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: AlertTriangle,
        title: 'Continuous Security Assessment',
        description: 'Ongoing security audits, penetration testing, and vulnerability management with remediation tracking',
        metrics: 'Continuous security validation',
        color: 'from-yellow-500 to-orange-600'
      }
    ]
  },

  'commercial-real-estate-platform': {
    keyBenefits: [
      {
        icon: Building,
        title: 'Enterprise Lease Administration',
        description: 'Comprehensive commercial lease management with automated rent escalations, CAM reconciliation, and compliance tracking',
        metrics: '60% lease administration efficiency',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Users,
        title: 'Tenant Relationship Excellence',
        description: 'Advanced tenant portal with service requests, communications, lease documents, and payment processing',
        metrics: '80% tenant satisfaction improvement',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: MapPin,
        title: 'Space Planning & Allocation',
        description: 'Intelligent space management with utilization analytics, floor plan optimization, and expansion planning',
        metrics: '25% space utilization improvement',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Calculator,
        title: 'CAM Billing & Financial Management',
        description: 'Automated Common Area Maintenance billing with expense allocation, reconciliation, and detailed reporting',
        metrics: '95% billing accuracy rate',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: BarChart3,
        title: 'Portfolio Performance Analytics',
        description: 'Real-time portfolio metrics with occupancy tracking, revenue analysis, and market benchmarking',
        metrics: '40% better investment decisions',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: TrendingUp,
        title: 'Capital Expenditure Optimization',
        description: 'Strategic CAPEX planning with project tracking, budget management, and ROI analysis for property improvements',
        metrics: '30% CAPEX efficiency gain',
        color: 'from-indigo-500 to-purple-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Cpu,
        title: 'AI-Powered Commercial Intelligence',
        description: 'Machine learning algorithms for market analysis, tenant risk assessment, and lease optimization strategies',
        metrics: 'Predictive commercial insights',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Database,
        title: 'Enterprise Property Data Hub',
        description: 'Centralized commercial property database with transaction history, market data, and tenant analytics',
        metrics: 'Complete commercial intelligence',
        color: 'from-emerald-500 to-teal-600'
      },
      {
        icon: Globe,
        title: 'Marketing & Leasing Platform',
        description: 'Professional property marketing with virtual tours, listing syndication, and lead management',
        metrics: '50% faster lease-up rates',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: FileText,
        title: 'Document & Compliance Management',
        description: 'Automated document generation, e-signature workflows, and regulatory compliance tracking',
        metrics: 'Streamlined documentation process',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Target,
        title: 'Strategic Portfolio Planning',
        description: 'Advanced portfolio analysis with scenario modeling, acquisition planning, and disposition strategies',
        metrics: 'Strategic portfolio optimization',
        color: 'from-orange-500 to-amber-600'
      },
      {
        icon: Network,
        title: 'Integration & API Platform',
        description: 'Seamless integrations with accounting systems, property management tools, and market data providers',
        metrics: 'Unified commercial ecosystem',
        color: 'from-pink-500 to-rose-600'
      }
    ],
    securityPerformance: [
      {
        icon: Award,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, GDPR, and industry-specific compliance with automated audit trails',
        metrics: 'Enterprise-grade compliance',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time security monitoring with threat detection, vulnerability assessments, and compliance reporting',
        metrics: '99.9% threat prevention',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Server,
        title: 'Scalable Security Infrastructure',
        description: 'Auto-scaling security architecture with load balancing, redundancy, and global content delivery',
        metrics: '99.99% uptime guarantee',
        color: 'from-purple-500 to-violet-600'
      },
      {
        icon: Shield,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Multi-layer security with intelligent threat detection, automated response, and traffic filtering',
        metrics: 'Impenetrable security barrier',
        color: 'from-red-500 to-rose-600'
      },
      {
        icon: Lock,
        title: 'Multi-Factor Authentication Suite',
        description: 'Comprehensive MFA with biometric options, hardware tokens, and adaptive authentication policies',
        metrics: '100% identity verification',
        color: 'from-orange-500 to-amber-600'
      },
      {
        icon: Eye,
        title: 'Security Information & Event Management',
        description: '24/7 SIEM monitoring with AI-powered anomaly detection and automated incident response',
        metrics: 'Proactive threat management',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Database,
        title: 'Data Loss Prevention & Backup',
        description: 'Encrypted data protection with automated backups, point-in-time recovery, and geo-redundancy',
        metrics: '100% data recovery guarantee',
        color: 'from-teal-500 to-emerald-600'
      },
      {
        icon: Network,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmented networks with zero-trust architecture and continuous traffic analysis',
        metrics: 'Zero-trust security model',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: AlertTriangle,
        title: 'Continuous Security Assessment',
        description: 'Ongoing security audits, penetration testing, and vulnerability management with remediation tracking',
        metrics: 'Continuous security validation',
        color: 'from-yellow-500 to-orange-600'
      }
    ]
  },

  'construction-project-tracker': {
    keyBenefits: [
      {
        icon: FileText,
        title: 'Comprehensive Project Timeline Management',
        description: 'Advanced construction scheduling with critical path analysis, milestone tracking, and real-time progress monitoring',
        metrics: '40% project timeline improvement',
        color: 'from-yellow-500 to-orange-600'
      },
      {
        icon: Calculator,
        title: 'Budget Tracking & Cost Control',
        description: 'Real-time budget monitoring with cost forecasting, change order management, and financial variance analysis',
        metrics: '95% budget accuracy maintenance',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Users,
        title: 'Contractor & Vendor Management',
        description: 'Comprehensive vendor coordination with performance tracking, payment scheduling, and qualification management',
        metrics: '30% vendor efficiency improvement',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: CheckCircle,
        title: 'Permit & Compliance Tracking',
        description: 'Automated permit management with regulatory compliance monitoring, inspection scheduling, and approval workflows',
        metrics: '100% compliance maintenance',
        color: 'from-purple-500 to-pink-600'
      },
      {
        icon: Database,
        title: 'Progress Documentation & Photos',
        description: 'Systematic progress documentation with photo management, quality control checklists, and milestone verification',
        metrics: 'Complete project visibility',
        color: 'from-orange-500 to-red-600'
      },
      {
        icon: BarChart3,
        title: 'Financial Reporting & Cash Flow',
        description: 'Advanced financial analytics with cash flow projections, profitability analysis, and investor reporting',
        metrics: '50% better financial control',
        color: 'from-cyan-500 to-teal-600'
      }
    ],
    advancedCapabilities: [
      {
        icon: Cpu,
        title: 'AI-Powered Project Intelligence',
        description: 'Machine learning algorithms for risk prediction, delay forecasting, and resource optimization',
        metrics: 'Predictive project management',
        color: 'from-violet-500 to-purple-600'
      },
      {
        icon: Smartphone,
        title: 'Mobile Construction Management',
        description: 'Native mobile apps for field teams with offline capability, real-time updates, and instant communication',
        metrics: 'Mobile project control',
        color: 'from-blue-500 to-cyan-600'
      },
      {
        icon: Target,
        title: 'Quality Control & Inspection System',
        description: 'Digital inspection workflows with automated quality checks, defect tracking, and remediation management',
        metrics: '99% quality standard compliance',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Globe,
        title: 'Change Order Management Platform',
        description: 'Streamlined change order processing with approval workflows, cost impact analysis, and documentation',
        metrics: 'Efficient change management',
        color: 'from-orange-500 to-amber-600'
      },
      {
        icon: Network,
        title: 'Resource & Material Optimization',
        description: 'Intelligent resource planning with material tracking, equipment scheduling, and labor optimization',
        metrics: '35% resource efficiency gain',
        color: 'from-pink-500 to-rose-600'
      },
      {
        icon: Search,
        title: 'Advanced Analytics & Reporting',
        description: 'Comprehensive project analytics with KPI dashboards, performance benchmarking, and predictive insights',
        metrics: 'Data-driven construction decisions',
        color: 'from-teal-500 to-emerald-600'
      }
    ],
    securityPerformance: [
      {
        icon: Award,
        title: 'Compliance Excellence Framework',
        description: 'SOC 2 Type II, ISO 27001, GDPR, and industry-specific compliance with automated audit trails',
        metrics: 'Enterprise-grade compliance',
        color: 'from-blue-500 to-indigo-600'
      },
      {
        icon: BarChart3,
        title: 'Security Performance Analytics',
        description: 'Real-time security monitoring with threat detection, vulnerability assessments, and compliance reporting',
        metrics: '99.9% threat prevention',
        color: 'from-green-500 to-emerald-600'
      },
      {
        icon: Server,
        title: 'Scalable Security Infrastructure',
        description: 'Auto-scaling security architecture with load balancing, redundancy, and global content delivery',
        metrics: '99.99% uptime guarantee',
        color: 'from-purple-500 to-violet-600'
      },
      {
        icon: Shield,
        title: 'Advanced Firewall & DDoS Protection',
        description: 'Multi-layer security with intelligent threat detection, automated response, and traffic filtering',
        metrics: 'Impenetrable security barrier',
        color: 'from-red-500 to-rose-600'
      },
      {
        icon: Lock,
        title: 'Multi-Factor Authentication Suite',
        description: 'Comprehensive MFA with biometric options, hardware tokens, and adaptive authentication policies',
        metrics: '100% identity verification',
        color: 'from-orange-500 to-amber-600'
      },
      {
        icon: Eye,
        title: 'Security Information & Event Management',
        description: '24/7 SIEM monitoring with AI-powered anomaly detection and automated incident response',
        metrics: 'Proactive threat management',
        color: 'from-cyan-500 to-blue-600'
      },
      {
        icon: Database,
        title: 'Data Loss Prevention & Backup',
        description: 'Encrypted data protection with automated backups, point-in-time recovery, and geo-redundancy',
        metrics: '100% data recovery guarantee',
        color: 'from-teal-500 to-emerald-600'
      },
      {
        icon: Network,
        title: 'Network Segmentation & Monitoring',
        description: 'Micro-segmented networks with zero-trust architecture and continuous traffic analysis',
        metrics: 'Zero-trust security model',
        color: 'from-indigo-500 to-purple-600'
      },
      {
        icon: AlertTriangle,
        title: 'Continuous Security Assessment',
        description: 'Ongoing security audits, penetration testing, and vulnerability management with remediation tracking',
        metrics: 'Continuous security validation',
        color: 'from-yellow-500 to-orange-600'
      }
    ]
  }
};